import { useEffect, useState } from "react";
import { COMPANY } from "@/data/company";
import { getPageSEO } from "@/services/api";

// ===========================================================================
// SEO — per-page <head> management for client-side navigation
// ---------------------------------------------------------------------------
// Crawlers and link-preview bots get their metadata from the `ssr` Cloud
// Function, which renders it into the HTML before it ever reaches the browser.
// This component keeps the same tags correct as a visitor moves between routes
// without a page load, and applies whatever the administrator has saved in
// SEO Pages — falling back to the props each page passes as sensible defaults.
// ===========================================================================
export default function SEO({
    title,
    description = "ProCare.SG — one-stop property care in Singapore. Moving, cleaning, painting, repairs, renovation and maintenance. Your MOVE. Our CARE.",
    keywords = "property services Singapore, moving services Singapore, move-in cleaning Singapore, painting services Singapore, one-stop property services Singapore",
    image = COMPANY.logo,
    path = "",
    type = "website",
    noindex = false,
}) {
    const [managed, setManaged] = useState(null);

    // Managed metadata only exists for the site's fixed pages; content pages
    // (blog articles, projects) pass their own values as props.
    useEffect(() => {
        let active = true;
        getPageSEO(path || window.location.pathname)
            .then((data) => active && setManaged(data))
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [path]);

    const resolvedTitle = title || managed?.title;
    const resolvedDescription = managed?.description || description;
    const resolvedKeywords = managed?.keywords || keywords;
    const resolvedImage = image || managed?.ogImage || COMPANY.logo;

    const fullTitle = !resolvedTitle
        ? `${COMPANY.name} — ${COMPANY.tagline}`
        : resolvedTitle.includes(COMPANY.name)
          ? resolvedTitle
          : `${resolvedTitle} | ${COMPANY.name}`;

    useEffect(() => {
        const origin = window.location.origin;
        const canonical = `${origin}${path || window.location.pathname}`;
        const absoluteImage = /^https?:\/\//i.test(resolvedImage)
            ? resolvedImage
            : `${origin}${resolvedImage.startsWith("/") ? "" : "/"}${resolvedImage}`;

        document.title = fullTitle;

        const setMeta = (attr, key, content) => {
            let element = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!element) {
                element = document.createElement("meta");
                element.setAttribute(attr, key);
                document.head.appendChild(element);
            }
            element.setAttribute("content", content);
        };

        setMeta("name", "description", resolvedDescription);
        setMeta("name", "keywords", resolvedKeywords);
        setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large");
        setMeta("property", "og:site_name", COMPANY.name);
        setMeta("property", "og:title", fullTitle);
        setMeta("property", "og:description", resolvedDescription);
        setMeta("property", "og:image", absoluteImage);
        setMeta("property", "og:type", type);
        setMeta("property", "og:url", canonical);
        setMeta("property", "og:locale", "en_SG");
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", fullTitle);
        setMeta("name", "twitter:description", resolvedDescription);
        setMeta("name", "twitter:image", absoluteImage);

        let link = document.head.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.setAttribute("rel", "canonical");
            document.head.appendChild(link);
        }
        link.setAttribute("href", canonical);
    }, [fullTitle, resolvedDescription, resolvedKeywords, resolvedImage, path, type, noindex]);

    return null;
}
